




"use client";

import React, { useEffect, useState } from "react";
import { useLanguage } from "../../app/components/context/LanguageContext";
import { useTheme } from "../context/ThemeContext";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import "swiper/css";
import Link from "next/link";

interface Product {
  id: number;
  name: string;
  description: string;
  category: string;
  price?: string;
  image?: string | null;
}

const translations: any = {
    en: {
    name: "Our Products",
    description: "An overview of our products designed to help you achieve optimal results.",
    loading: "Loading products...",
    button: "View all products",
  },

  fr: {
    name: "Nos Produits",
    description: "Un aperçu de nos produits conçus pour vous aider à obtenir des résultats optimaux.",
    loading: "Chargement des produits...",
    button: "Voir tous les produits",
  },

  ar: {
    name: "منتجاتنا",
    description: "نظرة عامة على منتجاتنا المصممة لمساعدتكم على تحقيق أفضل النتائج.",
    loading: "جاري تحميل المنتجات...",
    button: "عرض جميع المنتجات",
  },
};


const ProductsSection = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<string>("all");
  const { language } = useLanguage();
  const { theme } = useTheme();
     
  const t = translations[language] || translations.en;
  const STRAPI_URL = process.env.NEXT_PUBLIC_STRAPI_URL;

  const fetchProducts = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `${STRAPI_URL}/api/productsses?populate=*&locale=${language}`,
        { cache: "no-store" }
      );
      if (!res.ok) throw new Error("Failed to fetch products");

      const json = await res.json();
      const mapped: Product[] = json.data.map((item: any) => {
        const img = item.image;
        const imgUrl =
          img?.formats?.large?.url
            ? STRAPI_URL + img.formats.large.url
            : img?.url
            ? STRAPI_URL + img.url
            : "/images/default.jpg";

        return {
          id: item.id,
          name: item.name,
          description: item.description,
          category: item.category || "General",
          price: item.price,
          image: imgUrl,
        };
      });

      setProducts(mapped);
    } catch (err) {
      console.error("Error fetching products:", err);
      setError("Failed to load products");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchProducts();
  }, [language]);

  const categories = ["all", ...new Set(products.map((p) => p.category))];
  const filteredProducts =
    activeFilter === "all"
      ? products
      : products.filter((p) => p.category === activeFilter);

  const bgClass =
    theme === "dark"
      ? "bg-gradient-to-br from-gray-900 via-gray-800 to-blue-900/20"
      : "bg-gradient-to-br from-gray-50 via-white to-blue-50/30";

  if (loading)
    return (
      <section className={`py-20 ${bgClass}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 animate-pulse">
            <div className="h-12 bg-gray-300 dark:bg-gray-700 rounded-lg w-64 mx-auto mb-6">{t.Loading}</div>
            <div className="h-6 bg-gray-200 dark:bg-gray-600 rounded w-96 mx-auto"></div>
          </div>
        </div>
      </section>
    );

  if (error)
    return (
      <section className={`py-20 ${bgClass}`}>
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className={`text-red-600`}>{error}</p>
        </div>
      </section>
    );

  // Split products into chunks of 5
  const chunkedProducts: Product[][] = [];
  for (let i = 0; i < filteredProducts.length; i += 1 ) {
    chunkedProducts.push(filteredProducts.slice(i, i + 5));
  }

  return (
    <section className={`w-full py-20 relative overflow-hidden ${bgClass}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

      <div className="text-center mb-16">
        <h2 className={`text-4xl md:text-5xl font-semibold mb-6 ${
          theme === "dark" ? "text-white" : "text-gray-800"
        }`}>
          {t.name}
        </h2>
        <p className={`text-lg md:text-xl max-w-3xl mx-auto leading-relaxed ${
          theme === "dark" ? "text-gray-300" : "text-gray-600"
        }`}>
          {t.description}
        </p>
      </div>

   

        {/* Swiper with 5 products per slide */}
        <Swiper
          modules={[Autoplay]}
          loop
          autoplay={{ delay: 3000, disableOnInteraction: false }}
          spaceBetween={30}
        >
          {chunkedProducts.map((group, index) => (
            <SwiperSlide key={index}>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
                {group.map((product) => (
                  <div
                    key={product.id}
                    className={`group relative rounded-3xl overflow-hidden shadow-2xl hover:shadow-xl transition-all duration-500 flex flex-col h-full ${
                      theme === "dark" ? "bg-gray-800" : "bg-white"
                    }`}
                  >
                    <div className="relative w-full h-60 overflow-hidden">
                      <img
                        src={product.image || "/images/default.jpg"}
                        alt={product.name}
                        className="w-full h-full object-contain transition-transform duration-700 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-black/20"></div>
                    </div>
                    <div className="p-6 flex flex-col flex-1">
                      <h3
                        className={`font-bold text-lg mb-3 line-clamp-2 ${
                          theme === "dark" ? "text-gray-200" : "text-gray-900"
                        }`}
                      >
                        {product.name}
                      </h3>
                      <p
                        className={`text-sm mb-4 line-clamp-3 ${
                          theme === "dark" ? "text-gray-400" : "text-gray-600"
                        }`}
                      >
                        {product.description}
                      </p>
                      {product.price && (
                        <div className="text-right mb-4 font-semibold">{product.price}</div>
                      )}
                   
                    </div>
                  </div>
                ))}
              </div>
            </SwiperSlide>
          ))}
 
         </Swiper>
 
                           
 

         <div className="text-center mt-12">
          <Link href="/ProductsSection">
            <button
              className={`  px-6  py-3 rounded-xl font-semibold ${
                theme === "dark" ? "bg-blue-600 text-white" : "bg-gray-900 text-white"
              }`}
            >
            {t.button} 

            </button>
          </Link>
        </div>
        </div>
     
    </section>
  );
};

export default ProductsSection;









 